import * as React from "react";
import { useCombobox } from "downshift";
import { clsx } from "clsx";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
    IAutocompleteResponse,
    IAutocompleteResponseItem,
} from "../interfaces/search";
import { fetchAutocomplete } from "../api/searchapi";
import { IInitOptions } from "../interfaces/initoptions";
import { getInitOptions } from "../api/initoptions";
import IconSearch from "./icon/iconsearch";
import SuggestIcon from "./suggesticon";

export const useAutocomplete = (
    jurisdictionId: string,
    search: string,
): UseQueryResult<IAutocompleteResponse, Error> => {
    return useQuery({
        queryKey: ["autocomplete", jurisdictionId, search],
        queryFn: () => fetchAutocomplete(jurisdictionId, search),
    });
};

const initOptions: IInitOptions = getInitOptions();

const SearchBox = () => {
    const [searchValue, setSearchValue] = React.useState(
        initOptions.query || "",
    );
    const [searchError, setSearchError] = React.useState("");

    const { data, isLoading, error } = useAutocomplete(
        initOptions.jurisdictionId.toString(),
        searchValue,
    );

    const items: Array<IAutocompleteResponseItem> = data?.items || [];

    const {
        isOpen,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getItemProps,
        highlightedIndex,
        selectItem,
        selectedItem,
        inputValue,
    } = useCombobox({
        items,
        inputValue: searchValue,
        onInputValueChange: ({ inputValue: newValue }) => {
            setSearchValue(newValue || "");
            setSearchError("");
        },
        itemToString(item: IAutocompleteResponseItem) {
            return item ? item.text : "";
        },
    });

    const handleSearch = (searchText: string) => {
        const searchTrim = searchText.trim();
        if (searchTrim) {
            window.location.href = `${initOptions.resultUrl}?q=${
                encodeURIComponent(searchTrim)
            }`;
        } else {
            setSearchError("Please enter some search criteria");
            console.log("Empty");
        }
    };

    const handleKeyDown = (event, downshift) => {
        if (event.key === "Enter") {
            // Perform your action when the Enter key is pressed
            handleSearch(downshift.inputValue);
        }
    };

    let searchSuggest: JSX.Element | null = null;
    if (isOpen && items.length) {
        searchSuggest = (
            <ul {...getMenuProps()} className="facility-search-suggest">
                {items.map((item: IAutocompleteResponseItem, index) => (
                    <li
                        className={clsx(
                            highlightedIndex === index && "bg-primary-subtle",
                            selectedItem === item && "fw-bold",
                        )}
                        key={item.id}
                        {...getItemProps({
                            item,
                            index,
                            "data-testid": `downshift-item-${index}`,
                        })}
                    >
                        <SuggestIcon icon={item.icon} />
                        <span className="ms-2">{item.text}</span>
                    </li>
                ))}
            </ul>
        );
    }

    const classSearchBox = searchError
        ? "is-invalid facility-search-box"
        : "facility-search-box";

    return (
        <div className="facility-search">
            <div className={classSearchBox}>
                <div className="facility-search-button">
                    <button onClick={() => handleSearch(inputValue)}>
                        <IconSearch />
                    </button>
                </div>
                <input
                    type="search"
                    className="facility-search-input"
                    {...getInputProps({
                        placeholder: "Search by County, City, Zip or Name",
                        onKeyDown: (event) =>
                            handleKeyDown(event, { inputValue }),
                    })}
                />
            </div>
            {searchSuggest}
            {searchError && (
                <div className="facility-search-error">{searchError}</div>
            )}
        </div>
    );
};

export default SearchBox;
